import React from "react";
import { shallowEqual } from "react-redux";
import clsx from "clsx";
import { useCarConfigSelector } from "../../../../redux/store";
import { financeCtaSelector, pricingDetailsSelector } from "../../../../utils/selectorUtils";
import styles from "./Cta.module.scss";
import Text from "../../../../../shared-components/css-modules/lexus/atoms/Text";
import ExchangeRate from "../price/ExchangeRate";
import Button from "../../../../../shared-components/css-modules/lexus/atoms/Button";
import useAphModal from "../../../../hooks/useAphModal";
import { MODAL_FINANCE_CALCULATOR, MODAL_FINANCE_DISCLAIMER } from "../../../../utils/modalConstants";
import { formatMonthlyFinancePrice } from "../../../../../shared-logic/utils/priceUtils";
import { getAemFlexibilityMatrixConfig } from "../../../../../../../common-deprecated/settings/utils/financeSettingUtilsAem";
import { AemFmComponent } from "../../../../../../../common-deprecated/utils/aemFlexibilityMatrixUtils";
import { CarConfigLabel, useCarConfigOptionalLabel } from "../../../../utils/labelUtils";

const CtaDisclaimer = (): JSX.Element | null => {
    const financeOption = useCarConfigSelector((state) => state.utils.financeOption);
    const basketTotal = useCarConfigSelector((state) => state.basket.total);
    const { secondaryCurrencyCode } = useCarConfigSelector((state) => state.commonSettings.intl);
    const financeInfoDisclaimer = useCarConfigSelector((state) => state.finance.info.disclaimer);
    const section = useCarConfigSelector((state) => state.carConfigNavigation.activeSection);
    const { forceCashPrice, hideFinanceDetailsHeader, alwaysShowCashDisclaimer } = useCarConfigSelector(
        pricingDetailsSelector,
        shallowEqual,
    );
    const [carConfigCashPriceDisclaimer] = useCarConfigOptionalLabel(["carConfigCashPriceDisclaimer"]);

    const { showModalHelper } = useAphModal();

    const { total, commonSettings, info, modelCode, showFinance } = useCarConfigSelector(
        financeCtaSelector,
        shallowEqual,
    );

    const priceFormat =
        getAemFlexibilityMatrixConfig(commonSettings, AemFmComponent.Carconfig, "new", modelCode)?.config?.finance
            ?.format || "";

    const monthlyRateItems = formatMonthlyFinancePrice<string>(
        { ...basketTotal, monthly: Number(total) },
        info,
        commonSettings,
        priceFormat,
        false,
        basketTotal.monthly,
        true,
    );

    const isCashSelected = financeOption === "cash";

    if (!showFinance) return null;

    return (
        <>
            {financeOption === "cash" && (
                <>
                    {/* Cash price disclaimer */}
                    {alwaysShowCashDisclaimer && (
                        <div className={styles.disclaimer}>
                            <div className={styles["cash-representative-disclaimer"]}>
                                <Text variant="apheleia-h5">{carConfigCashPriceDisclaimer}</Text>
                            </div>
                        </div>
                    )}
                    {secondaryCurrencyCode && <ExchangeRate />}
                </>
            )}
            {/* Monthly price disclaimer */}
            {(!isCashSelected || forceCashPrice) && (financeInfoDisclaimer || secondaryCurrencyCode) && (
                <div className={styles.disclaimer}>
                    <div className={styles["finance-representative-disclaimer"]}>
                        {secondaryCurrencyCode && <ExchangeRate monthly />}
                        <div
                            className={clsx(
                                !isCashSelected &&
                                    (forceCashPrice || info?.productName) &&
                                    styles["multiple-disclaimer-items"],
                            )}
                        >
                            {forceCashPrice ? (
                                <span dangerouslySetInnerHTML={{ __html: monthlyRateItems }} />
                            ) : (
                                info?.productName && (
                                    <CarConfigLabel
                                        label="carConfigSelectedFinancePlan"
                                        params={{ financeProductName: info.productName }}
                                    />
                                )
                            )}

                            {!isCashSelected && (
                                <>
                                    {(forceCashPrice || info?.productName) && (
                                        <span className={styles["disclaimer-divider"]} />
                                    )}
                                    <Button
                                        className={styles["edit-plan-button"]}
                                        variant="apheleia-text"
                                        onClick={() =>
                                            showModalHelper(MODAL_FINANCE_CALCULATOR, {
                                                step: section,
                                                origin: "cc-navigation-top",
                                            })
                                        }
                                    >
                                        <CarConfigLabel label="carConfigEditFinancePlan" />
                                    </Button>
                                </>
                            )}
                        </div>
                        {!hideFinanceDetailsHeader && (
                            <Button
                                variant="apheleia-text"
                                onClick={() => showModalHelper(MODAL_FINANCE_DISCLAIMER, {})}
                            >
                                <CarConfigLabel label="carConfigReadCoreFinanceTerms" />
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default CtaDisclaimer;
